/**
 * Content Equalizer Web Component
 * Automatically equalizes the height of content elements within a container
 */
class ContentEqualizer extends HTMLElement {
  constructor() {
    super();
    this.resizeTimeout = null;
  }

  connectedCallback() {
    this.bindEvents();
    this.equalizeContentHeights();
  }

  disconnectedCallback() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  bindEvents() {
    // Debounced resize handler
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.equalizeContentHeights();
      }, 150);
    });

    // Equalize heights when images load
    const images = this.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        this.equalizeContentHeights();
      } else {
        img.addEventListener('load', () => this.equalizeContentHeights(), { once: true });
      }
    });
  }

  equalizeContentHeights() {
    // Check if we're on mobile and equalizer is disabled on mobile
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (isMobile) {
      const groupBlock = this.closest('.group-block');
      if (groupBlock && groupBlock.dataset.disableEqualizerMobile === 'true') {
        // Reset all min-heights and return early
        const textSelectors = [
          'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          '.testimonial-text', '.quote-text', '.card-text',
          '[class*="text"]', '[class*="content"]'
        ];
        textSelectors.forEach(selector => {
          this.querySelectorAll(selector).forEach(el => {
            el.style.minHeight = '';
          });
        });
        return;
      }
    }

    // Get section ID to find all equalizers in the same section
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    // Find all equalizers in the same section that have equalizer enabled
    let sectionEqualizers = Array.from(document.querySelectorAll(`[data-section-id="${sectionId}"][data-equalizer-type="group-content"]`));
    
    // On mobile, filter out equalizers that have mobile disabled
    if (isMobile) {
      sectionEqualizers = sectionEqualizers.filter(equalizer => {
        const groupBlock = equalizer.closest('.group-block');
        return !groupBlock || groupBlock.dataset.disableEqualizerMobile !== 'true';
      });
    }
    
    if (sectionEqualizers.length < 2) return;

    // Find all text content elements that need equalizing
    const textSelectors = [
      'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      '.testimonial-text', '.quote-text', '.card-text',
      '[class*="text"]', '[class*="content"]'
    ];

    // Group elements by their selector type
    const elementGroups = {};

    textSelectors.forEach(selector => {
      const elements = [];
      sectionEqualizers.forEach(equalizer => {
        const equalizerElements = equalizer.querySelectorAll(selector);
        elements.push(...Array.from(equalizerElements));
      });

      if (elements.length > 0) {
        elementGroups[selector] = elements;
      }
    });

    // Equalize each group of elements separately
    Object.entries(elementGroups).forEach(([selector, elements]) => {
      if (elements.length < 2) {
        // If only one element, remove any min-height and return
        elements.forEach(el => {
          el.style.minHeight = '';
        });
        return;
      }

      // Reset min-heights to get natural heights
      elements.forEach(el => {
        el.style.minHeight = '';
      });

      // Force a reflow
      this.offsetHeight;

      // Group elements by their position (nth-child) within each equalizer
      const positionGroups = {};

      sectionEqualizers.forEach(equalizer => {
        const equalizerElements = Array.from(equalizer.querySelectorAll(selector));
        equalizerElements.forEach((el, index) => {
          const position = index + 1; // nth-child is 1-based
          if (!positionGroups[position]) {
            positionGroups[position] = [];
          }
          positionGroups[position].push(el);
        });
      });

      // Equalize elements at the same position
      Object.values(positionGroups).forEach(positionElements => {
        if (positionElements.length < 2) {
          // Single element at this position, remove min-height
          positionElements.forEach(el => {
            el.style.minHeight = '';
          });
          return;
        }

        // Find max height for this position
        let maxHeight = 0;
        positionElements.forEach(el => {
          const height = el.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });

        // Set all elements at this position to max height (except if it's the last position)
        const isLastPosition = positionElements.some(el => {
          const equalizer = el.closest('[data-equalizer-type="group-content"]');
          const allElements = Array.from(equalizer.querySelectorAll(selector));
          return el === allElements[allElements.length - 1];
        });

        if (!isLastPosition) {
          positionElements.forEach(el => {
            el.style.minHeight = `${maxHeight}px`;
          });
        }
      });
    });
  }
}

// Register the custom element only if it hasn't been defined already
if (!customElements.get('content-equalizer')) {
  customElements.define('content-equalizer', ContentEqualizer);
}

// Auto-initialize content equalizers for group blocks
document.addEventListener('DOMContentLoaded', () => {
  // Find all group blocks
  const groupBlocks = document.querySelectorAll('.group-block');

  groupBlocks.forEach(groupBlock => {
    // Skip if already initialized
    if (groupBlock.classList.contains('content-equalizer-initialized')) {
      return;
    }

    // Get section and block info for debugging
    const sectionId = groupBlock.dataset.sectionId || 'unknown';
    const blockId = groupBlock.dataset.blockId || 'unknown';

    // Find the group-block-content div
    const contentDiv = groupBlock.querySelector('.group-block-content');
    if (!contentDiv) return;

    // Check if this group block contains actual content (not just nested group blocks)
    const hasRealContent = Array.from(contentDiv.children).some(child => {
      // Check if child is not a group block
      if (!child.classList.contains('group-block')) {
        return true;
      }

      // If it is a group block, check if it has any non-group-block content
      const nestedContent = child.querySelector('.group-block-content');
      if (nestedContent) {
        return Array.from(nestedContent.children).some(nestedChild => {
          return !nestedChild.classList.contains('group-block');
        });
      }

      return false;
    });

    // Only initialize if it has real content and equalizer is enabled
    const equalizerEnabled = groupBlock.dataset.enableEqualizer === 'true';
    if (hasRealContent && equalizerEnabled) {
      // Wrap the content in a content-equalizer
      const equalizer = document.createElement('content-equalizer');
      equalizer.className = contentDiv.className;
      equalizer.style.cssText = contentDiv.style.cssText;

      // Add data attributes for tracking
      equalizer.dataset.sectionId = sectionId;
      equalizer.dataset.blockId = blockId;
      equalizer.dataset.equalizerType = 'group-content';

      // Move all children from contentDiv to equalizer
      while (contentDiv.firstChild) {
        equalizer.appendChild(contentDiv.firstChild);
      }

      // Replace contentDiv with equalizer
      contentDiv.parentNode.replaceChild(equalizer, contentDiv);

      // Mark as initialized to prevent double initialization
      groupBlock.classList.add('content-equalizer-initialized');
    }
  });
});
